import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Crown, Heart, Loader2, Share2, Zap } from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { millionsAudio } from "@/lib/millionsAudio";
import { toast } from "sonner";

const Scoreboard = ({ rows, me }) => (
  <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-3" data-testid="clash-scoreboard">
    <div className="text-xs font-bold text-white/50 mb-2">SCOREBOARD</div>
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <div key={r.player_id}
          className={`flex items-center gap-2 text-sm rounded-lg px-2 py-1 ${r.player_id === me ? "bg-[#00E0C7]/10 border border-[#00E0C7]/30" : ""} ${r.eliminated_at != null ? "opacity-40" : ""}`}
          data-testid={`clash-score-${r.player_id}`}>
          <span className="w-4 text-center font-extrabold text-[#FFB42D] text-xs">{i + 1}</span>
          <span className="font-bold truncate flex-1">@{r.player?.handle}
            <span className="text-[9px] text-white/40 ml-1">Lv{r.player?.level}</span>
          </span>
          {r.eliminated_at != null ? (
            <span className="text-[10px] font-bold text-[#FF2D7A]">OUT</span>
          ) : (
            <span className="flex items-center gap-0.5">
              {Array.from({ length: r.lives }).map((_, k) => <Heart key={k} className="w-3 h-3 fill-[#FF2D7A] text-[#FF2D7A]" />)}
            </span>
          )}
          {r.streak >= 3 && <span className="text-[10px] font-bold text-[#FFB42D]">🔥x{r.streak >= 5 ? 2 : 1.5}</span>}
          <span className="font-extrabold text-sm w-14 text-right">{r.score}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function PlyxClashRoom() {
  const { code } = useParams();
  const { user } = usePlyxAuth();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [missing, setMissing] = useState(false);
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const lastQRef = useRef(-1);
  const revealPlayedRef = useRef(-1);

  const load = useCallback(() => {
    api.get(`/plyx/clash/room/${code}`)
      .then((r) => {
        setRoom(r.data);
        setSecs(r.data.seconds_left);
        if (r.data.q_index !== lastQRef.current && r.data.phase === "question") {
          lastQRef.current = r.data.q_index;
          setPicked(null);
        }
        if (r.data.phase === "reveal" && revealPlayedRef.current !== r.data.q_index) {
          revealPlayedRef.current = r.data.q_index;
          const mine = r.data.my_choice;
          if (mine != null) {
            if (mine === r.data.correct_index) millionsAudio.sfxCorrect(); else millionsAudio.sfxWrong();
          }
        }
      })
      .catch((e) => { if (e?.response?.status === 404) setMissing(true); });
  }, [code]);

  useEffect(() => {
    load();
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setSecs((s) => {
      const n = Math.max(0, s - 1);
      if (room?.phase === "question" && n <= 5 && n > 0) millionsAudio.tick(true);
      return n;
    }), 1000);
    return () => clearInterval(t);
  }, [room?.phase]);

  const act = async (path, okMsg) => {
    if (!user) { navigate("/plyx/login"); return; }
    millionsAudio.ensure();
    setBusy(true);
    try {
      await api.post(`/plyx/clash/room/${code}/${path}`);
      if (okMsg) toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const answer = async (choice) => {
    if (picked != null || room.my_choice != null) return;
    millionsAudio.ensure();
    setPicked(choice);
    try {
      await api.post(`/plyx/clash/room/${code}/answer`, { q_index: room.q_index, choice });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Answer not accepted");
    }
  };

  const share = () => {
    const url = `${window.location.origin}/plyx/clash/room/${code}`;
    if (navigator.share) navigator.share({ title: `Join my Brain Clash room — code ${code}`, url }).catch(() => {});
    else { navigator.clipboard?.writeText(url); toast.success("Room link copied — send it to your people!"); }
  };

  if (missing) {
    return (
      <div className="min-h-screen millions-stage text-white flex flex-col items-center justify-center gap-3" data-testid="clash-room-missing">
        <div className="text-4xl">🧠</div>
        <p className="font-bold">Room not found</p>
        <Link to="/plyx/clash" className="text-sm text-[#00E0C7] underline">Back to Brain Clash</Link>
      </div>
    );
  }
  if (!room) {
    return (
      <div className="min-h-screen millions-stage text-white flex items-center justify-center" data-testid="clash-room-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Joining room…
      </div>
    );
  }

  const pid = user?.plyx_user_id;
  const myRow = room.scoreboard.find((r) => r.player_id === pid);
  const eliminated = myRow && myRow.eliminated_at != null;

  return (
    <div className="min-h-screen millions-stage text-white pb-24" data-testid="clash-room">
      <div className="max-w-2xl mx-auto px-4 pt-5">
        <div className="flex items-center justify-between mb-3">
          <Link to="/plyx/clash" className="flex items-center gap-1 text-white/60 text-sm" data-testid="clash-room-back">
            <ArrowLeft className="w-4 h-4" /> Brain Clash
          </Link>
          <div className="text-xs font-extrabold tracking-widest px-3 py-1 rounded-full bg-white/10" data-testid="clash-room-code">
            {room.code} · {room.category_label}
          </div>
          <button type="button" onClick={share} data-testid="clash-room-share"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-xs font-bold">
            <Share2 className="w-3.5 h-3.5" /> Invite
          </button>
        </div>

        {room.status === "lobby" && (
          <div data-testid="clash-lobby">
            <h1 className="text-2xl font-extrabold mb-1">Waiting room 🧠</h1>
            <p className="text-sm text-white/60 mb-4">
              {room.scoreboard.length}/{room.max_players} players in. Share code <span className="font-extrabold text-[#00E0C7]">{room.code}</span> — the host starts when everyone's ready.
            </p>
            <div className="flex flex-wrap gap-2 mb-5">
              {room.scoreboard.map((r) => (
                <span key={r.player_id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-sm font-bold">
                  @{r.player?.handle} <span className="text-[9px] text-[#FFB42D]">Lv{r.player?.level}</span>
                  {r.player_id === room.host_id && <Crown className="w-3 h-3 text-[#FFB42D]" />}
                </span>
              ))}
            </div>
            {!room.is_member && (
              <button type="button" onClick={() => act("join", "You're in!")} disabled={busy} data-testid="clash-join-room-btn"
                className="w-full py-3.5 rounded-full bg-[#00E0C7] text-black font-extrabold mb-2 disabled:opacity-50">
                Join this room
              </button>
            )}
            {room.is_host && (
              <button type="button" onClick={() => act("start")} disabled={busy} data-testid="clash-start-btn"
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-black font-extrabold disabled:opacity-50">
                🚀 Start the clash ({room.scoreboard.length} player{room.scoreboard.length === 1 ? "" : "s"})
              </button>
            )}
            {room.is_member && !room.is_host && (
              <p className="text-center text-sm text-white/50" data-testid="clash-waiting-host">Waiting for the host to start…</p>
            )}
          </div>
        )}

        {room.status === "live" && room.phase === "countdown" && (
          <div className="text-center py-24" data-testid="clash-countdown">
            <div className="text-7xl font-extrabold text-[#FFB42D] animate-pulse">{Math.ceil(secs)}</div>
            <p className="text-white/60 mt-3">Get ready…</p>
          </div>
        )}

        {room.status === "live" && (room.phase === "question" || room.phase === "reveal") && room.question && (
          <div data-testid="clash-play">
            {eliminated && (
              <div className="rounded-xl bg-[#FF2D7A]/15 border border-[#FF2D7A]/40 text-center text-sm font-bold py-2 mb-3" data-testid="clash-eliminated-banner">
                💀 Out of lives — spectating the rest of the clash
              </div>
            )}
            <div className="flex items-center justify-between text-xs text-white/50 mb-1.5">
              <span>Question {room.question.number} of {room.question.total}</span>
              <span className="font-extrabold text-white">{room.phase === "question" ? `${Math.ceil(secs)}s` : "Answers locked"}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 mb-3 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-1000 ${secs <= 5 ? "bg-[#FF2D7A]" : "bg-[#00E0C7]"}`}
                style={{ width: room.phase === "question" ? `${(secs / room.question_secs) * 100}%` : "0%" }} />
            </div>
            <div className="rounded-2xl millions-qcard p-5 mb-4">
              <h2 className="text-lg font-extrabold leading-snug" data-testid="clash-question">{room.question.q}</h2>
            </div>
            <div className="grid gap-2.5 mb-4">
              {room.question.options.map((opt, i) => {
                const mine = picked != null ? picked : room.my_choice;
                const isMine = mine === i;
                const isCorrect = room.phase === "reveal" && room.correct_index === i;
                const isWrongPick = room.phase === "reveal" && isMine && room.correct_index !== i;
                return (
                  <button key={i} type="button" disabled={room.phase !== "question" || mine != null || eliminated || !room.is_member}
                    onClick={() => answer(i)} data-testid={`clash-option-${i}`}
                    className={`text-left px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                      isCorrect ? "bg-[#00E0C7]/20 border-[#00E0C7] text-[#00E0C7]"
                      : isWrongPick ? "bg-[#FF2D7A]/20 border-[#FF2D7A] text-[#FF2D7A]"
                      : isMine ? "bg-[#FFB42D]/20 border-[#FFB42D]"
                      : "bg-white/[0.06] border-white/10 hover:border-white/30 disabled:opacity-60"
                    }`}>
                    <span className="text-[#FFB42D] font-extrabold mr-2">{"ABCD"[i]}.</span>{opt}
                  </button>
                );
              })}
            </div>
            {room.phase === "question" && (picked != null || room.my_choice != null) && (
              <p className="text-center text-xs text-[#00E0C7] font-bold mb-3" data-testid="clash-locked">✔ Answer locked — waiting for the room…</p>
            )}
            <Scoreboard rows={room.scoreboard} me={pid} />
          </div>
        )}

        {room.status === "finished" && (
          <div data-testid="clash-finished">
            <div className="text-center mb-5">
              <div className="text-6xl mb-2">🏆</div>
              <h1 className="text-2xl font-extrabold">Clash complete!</h1>
              {room.scoreboard[0] && (
                <p className="text-sm text-white/60 mt-1">
                  <span className="font-extrabold text-[#FFB42D]">@{room.scoreboard[0].player?.handle}</span> takes the crown with {room.scoreboard[0].score} points
                </p>
              )}
            </div>
            <div className="space-y-2 mb-5">
              {room.scoreboard.map((r, i) => (
                <div key={r.player_id}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border ${i === 0 ? "bg-[#FFB42D]/10 border-[#FFB42D]/40" : "bg-white/[0.04] border-white/10"} ${r.player_id === pid ? "ring-1 ring-[#00E0C7]/50" : ""}`}
                  data-testid={`clash-result-${r.player_id}`}>
                  <span className="text-lg">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`}</span>
                  <span className="font-bold flex-1 truncate">@{r.player?.handle}</span>
                  {r.xp_awarded != null && <span className="text-xs font-bold text-[#00E0C7] inline-flex items-center gap-0.5"><Zap className="w-3 h-3" />+{r.xp_awarded} XP</span>}
                  <span className="font-extrabold">{r.score}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3 justify-center">
              <Link to="/plyx/clash" data-testid="clash-play-again"
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#00E0C7] to-[#1a7bff] text-black font-extrabold text-sm">Host another</Link>
              <Link to="/plyx/games" data-testid="clash-to-games"
                className="px-6 py-2.5 rounded-full bg-white/10 font-bold text-sm">All games</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
